'use client';

import React, { useEffect, useRef } from 'react';

const themes = [
  {
    year: '2026',
    theme: 'Prathaa',
    bengali: 'প্রথা',
    desc: 'Tradition - Living, Not Remembered. Gala Evening at Shanmukhananda Chandrasekhara Saraswati Auditorium. 1,000 memberships targeted. Our biggest, most ambitious chapter yet.',
    color: 'bg-primary/5 border-primary/20',
    accent: 'text-primary',
    dot: 'bg-primary',
    current: true,
  },
  {
    year: '2025',
    theme: 'Patachitra',
    bengali: 'পটচিত্র',
    desc: 'Inaugurated by Maharaj, Ramakrishna Mission. 450 members. 2,000 sit-down Bhog plates. Chandrabindoo and Rituparna Sengupta graced the stage.',
    color: 'bg-rose-50 border-rose-200',
    accent: 'text-rose-700',
    dot: 'bg-rose-400',
  },
  {
    year: '2024',
    theme: 'Thakur Dalaan',
    bengali: 'ঠাকুর দালান',
    desc: 'Inaugurated by His Grace Gauranga Das, ISKCON. An intimate, deeply personal first - 1,000+ sit-down Bhog plates, warm community spirit.',
    color: 'bg-amber-50 border-amber-200',
    accent: 'text-amber-700',
    dot: 'bg-amber-400',
  },
];

export default function JourneySection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('active'); });
      },
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll('.reveal')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-muted/40 w-full px-4 md:px-12 pt-14 md:pt-20 pb-12 md:pb-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start">
          {/* Left: Header */}
          <div className="lg:col-span-4 reveal">
            <span className="inline-block px-4 py-1.5 rounded-full border border-border text-muted-foreground text-xs font-bold tracking-widest uppercase mb-4 md:mb-5">
              Our Journey
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-foreground tracking-tight leading-tight mb-4 md:mb-6">
              Three Years.<br />
              <span className="text-primary">Three Themes.</span>
            </h2>
            <p className="text-muted-foreground text-sm md:text-base leading-relaxed mb-5 md:mb-6">
              HCAF began as a simple, soulful Saraswati Puja in 2022 and has grown into one of Mumbai&apos;s most distinctive and authentic Bengali cultural platforms.
            </p>
            <div className="flex flex-row lg:flex-col gap-4 lg:gap-3 flex-wrap">
              {[
                { num: '450+', label: 'Members in 2025' },
                { num: '10,000+', label: 'Footfall across 5 days' },
                { num: '2,000+', label: 'Sit-down Bhog plates' },
              ]?.map((stat) => (
                <div key={stat?.num} className="flex items-center gap-2 md:gap-3">
                  <span className="font-display text-xl md:text-2xl font-black text-primary">{stat?.num}</span>
                  <span className="text-muted-foreground text-xs md:text-sm">{stat?.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Theme Cards */}
          <div className="lg:col-span-8 flex flex-col gap-4 md:gap-5">
            {themes?.map((t, i) => (
              <div
                key={t?.year}
                className={`reveal border rounded-2xl md:rounded-3xl p-5 md:p-7 ${t?.color} relative overflow-hidden transition-all duration-300 hover:shadow-gold`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                {t?.current && (
                  <span className="absolute top-4 right-4 md:top-5 md:right-5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold tracking-widest uppercase">
                    Current
                  </span>
                )}
                <div className="flex items-start gap-4 md:gap-5">
                  <div className="flex flex-col items-center gap-2 flex-shrink-0">
                    <div className={`w-3 h-3 md:w-4 md:h-4 rounded-full ${t?.dot} flex-shrink-0`} />
                    {i < themes?.length - 1 && (
                      <div className="w-0.5 h-10 md:h-12 bg-border" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2 md:gap-3 mb-1">
                      <span className={`font-display text-2xl md:text-3xl font-black ${t?.accent}`}>{t?.year}</span>
                      <span className="font-bold text-foreground text-base md:text-lg">{t?.theme}</span>
                      <span className="text-muted-foreground text-sm md:text-base" style={{ fontFamily: "'Noto Serif Bengali', serif" }}>{t?.bengali}</span>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">{t?.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}